import React from 'react';
import MaterialInputAdornment from '@mui/material/InputAdornment';
import type {InputAdornmentProps as MaterialInputAdornmentProps} from '@mui/material/InputAdornment';
import {styled} from '@mui/material/styles';
import clsx from 'clsx';
import {excludeComponentProps} from '../utils/excludeComponentProps';

export interface InputAdornmentProps extends Omit<MaterialInputAdornmentProps, 'variant'> {
    /**
     * used for removing extra padding
     */
    noPadding?: boolean;
}

const StyledInputAdornment = styled(MaterialInputAdornment, {
    ...excludeComponentProps('noPadding')
})<InputAdornmentProps>(({
    position, noPadding
}) => ({
    height: '100%',

    ...position === 'start' && {
        paddingLeft: noPadding ? 0 : '0.5rem'
    },

    ...position === 'end' && {
        paddingRight: noPadding ? 0 : '0.5rem'
    },

    '& .InputAdornment-adornment': {
        padding: '0 0.25rem',
        height: '100%',
        display: 'flex',
        alignItems: 'center',

        '&.InputAdornment-firstAdornment': {
            paddingLeft: 0
        },

        '&.InputAdornment-lastAdornment': {
            paddingRight: 0
        }
    }

}));

export const InputAdornment = React.forwardRef<HTMLDivElement, InputAdornmentProps>(({
    children, position, ...props
}, ref) => {
    const childrenCount = React.Children.count(children);

    return (
        <StyledInputAdornment
            {...props}
            position={position}
            ref={ref}
            variant={undefined}
        >
            {React.Children.map(children, (child, index) => {
                return React.cloneElement(
                    <div
                        className={clsx({
                            'InputAdornment-firstAdornment': Boolean(position === 'start' && index === 0),
                            'InputAdornment-adornment': true,
                            'InputAdornment-lastAdornment': Boolean(position === 'end' && index === childrenCount - 1)
                        })}
                    >{child}</div>
                );
            })}
        </StyledInputAdornment>
    );
});

InputAdornment.displayName = 'InputAdornment';
